import React, { useState, useEffect, useRef } from 'react';
import {
  onHoverHistogram,
  onClickHistogram,
  useGlobalSelectedId,
  useGlobalSelected10Ids,
  useGlobalHoveredId,
  onSetActiveIndicator,
  onSetActiveModuleId,
  onSetActiveDBModuleId,
  onSetActiveRGModuleId,
  onSetActiveSTModuleId, 
  useGlobalActivePage,
  useGlobalActiveGeography,
  useGlobalActiveIndicator,
  onSetSelected10Ids,
} from '../../data/StatusStore';

import { d2gRound, getTooltipGeoText } from '../utilities/Utilities';
import Colors from '../utilities/Colors';
import COTMeta from './COTMeta';

export interface BarChartProps {
  options: any;
  data: any;
  nycData: any;
  width: number;
  height: number;
  colorClass: string;
}

// Margins so nothing gets cut off + space for labels
const MARGIN_LEFT = 60;
const MARGIN_RIGHT = 20;
const MARGIN_TOP = 20;
const MARGIN_BOTTOM = 70; // Enough room for the year box

// Reduced from 500 to 400 for a shorter chart
const SVG_BASE_WIDTH = 800;
const SVG_BASE_HEIGHT = 400;

/**
 * Parse the 4-digit year from a variable name, e.g. "ahdi_cot_2017" => 2017
 */
function parseYearSuffix(variable: string): number | null {
  const match = variable.match(/\d{4}$/);
  if (match) {
    return parseInt(match[0]);
  }
  return null;
}

const COT: React.FC<BarChartProps> = ({ options, data, nycData, width, height, colorClass }) => {
  const [allLineData, setAllLineData] = useState<any[]>([]);
  const [avgLineData, setAvgLineData] = useState<{ year: number; value: number | null, descriptor:string  }[]>([]);
  const [years, setYears] = useState<any[]>([]);
  const [selectedYear, setSelectedYear] = useState<any>(null);
  const [globalMinValue, setGlobalMinValue] = useState<number>(0);
  const [globalMaxValue, setGlobalMaxValue] = useState<number>(10);
  const [selectedIndicator, setSelectedIndicator] = useState<any>(options?.variables[0]);
  const [leastChangeActive, setLeastChangeActive] = useState<any>(false);
  const [mostChangeActive, setMostChangeActive] = useState<any>(false);
  const [yearRange, setYearRange] = useState<any>([null, null]);
  const [change, setChange] = useState<number | null>(null);
  const [inViewport, setInViewport] = useState<boolean>(false);
  // For coloring dots by year-based quintiles
  const [yearValueMap, setYearValueMap] = useState<{ [year: number]: number[] }>({});

  // For tooltip display
  const [tooltip, setTooltip] = useState<{ x: number; y: number; obj: any } | null>(null);

  // Local hovered line state (strategy similar to HistogramDynamic.tsx)
  const [localHoveredLine, setLocalHoveredLine] = useState<any>(null);

  // Use a ref for container bounds (for tooltip positioning)
  const containerRef = useRef<HTMLDivElement | null>(null);

  // From the global store
  const selectedId = useGlobalSelectedId();
  const selected10Ids = useGlobalSelected10Ids();
  const hoveredId = useGlobalHoveredId();
  const activePage = useGlobalActivePage();
  const activeIndicator = useGlobalActiveIndicator();
  const activeGeography = useGlobalActiveGeography();

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        const isVisible = entry.isIntersecting;
        setInViewport(isVisible);
        if (isVisible) {
          console.log('COT entered viewport');
        } else {
          console.log('COT exited viewport');
        }
      },
      {
        root: null,        // viewport
        rootMargin: '0px',
        threshold: 0.25    // trigger around 25% visible
      }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    console.log("C121025 COT inViewport", inViewport);
    if (!inViewport){
      setLeastChangeActive(false);
      setMostChangeActive(false);
    }
    // do on-enter actions if needed
  }, [inViewport]);

  // Update hovered line locally when hoveredId or allLineData changes
  useEffect(() => {
    console.log("A042225 selectedYear", selectedYear);
  }, [selectedYear]);

  useEffect(() => {
    //console.log("A042425 activeIndicator", activeIndicator);
    const matchedVariable = options.variables.find((v: any) => v.variable === activeIndicator);
    //console.log("A042425 matchedVariable", matchedVariable);
    if (!matchedVariable){
      setSelectedYear(null);
    }
  }, [activeIndicator]);

  // Update hovered line locally when hoveredId or allLineData changes
  useEffect(() => {
    console.log("040625 COT hoveredId", hoveredId);
    const hl = allLineData.find((l) => l.id === hoveredId?.toString());
    setLocalHoveredLine(hl);
  }, [hoveredId, allLineData]);

  // On mount / data changes, prep the line data
  useEffect(() => {
    if (data && data.dataJSON) {
      prepData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    console.log("C122825 mostChangeActive, leastChangeActive", mostChangeActive, leastChangeActive );
    console.log("C122825 options, data", options, data );
    console.log("C122825 activeIndicator", activeIndicator );
    //onSetActiveIndicator(chartIndicator[0].variable, "category header", "N/A");
    if (mostChangeActive){
      if (yearRange[0] && yearRange[1]) {
        const changes: { geoId: string; change: number }[] = [];
        data.dataArray.GEO_ID.forEach((item: any) => {
          if (item.id) {
            let geoId = item.id;
            let firstYear = data.keyIndexDataArray[yearRange[0].variable][geoId].value;
            let lastYear = data.keyIndexDataArray[yearRange[1].variable][geoId].value;
            let _change = ((lastYear - firstYear) / firstYear) * 100;
            changes.push({ geoId, change: _change });
          }
        });

        // Sort changes in descending order and keep the top 10
        const topChanges = changes
          .sort((a, b) => Math.abs(b.change) - Math.abs(a.change))
          .slice(0, 10);

        console.log("E040825 topChanges", topChanges);
        onSetSelected10Ids(topChanges);
      }
    }
    if (leastChangeActive){
      if (yearRange[0] && yearRange[1]) {
        const changes: { geoId: string; change: number }[] = [];
        data.dataArray.GEO_ID.forEach((item: any) => {
          if (item.id) {
            let geoId = item.id;
            let firstYear = data.keyIndexDataArray[yearRange[0].variable][geoId].value;
            let lastYear = data.keyIndexDataArray[yearRange[1].variable][geoId].value;
            let _change = ((lastYear - firstYear) / firstYear) * 100;
            changes.push({ geoId, change: _change });
          }
        });

        // Sort changes in descending order and keep the top 10
        const bottomChanges = changes
          .sort((b, a) => Math.abs(b.change) - Math.abs(a.change))
          .slice(0, 10);

        console.log("E040825 bottomChanges", bottomChanges);
        onSetSelected10Ids(bottomChanges);
      }
    }
    if (!mostChangeActive && !leastChangeActive) {
      onSetSelected10Ids([]);
    }
  }, [mostChangeActive, leastChangeActive]);

  useEffect(() => {
    console.log("E040825 COT selected10Ids", selected10Ids);
  }, [selected10Ids]);

  useEffect(() => {
    console.log("A042225 tooltip", tooltip);
  }, [tooltip]);

  /**
   * Gather:
   *   - unique years from options.variables
   *   - line data for each record ID in data.dataJSON (using your snippet)
   *   - NYC average line
   *   - min/max value for scaling
   *   - yearValueMap for distributing the dot color by year-based quintiles
   */
  const prepData = () => {
    if (!options?.variables || !data?.dataJSON) return;

    const distinctYears: any[] = [];
    let minVal = Number.POSITIVE_INFINITY;
    let maxVal = Number.NEGATIVE_INFINITY;

    // Prepare an object to store all values for each year => so we can rank each dot
    const yMap: { [year: number]: number[] } = {};

    // Collect distinct years from the variables
    options.variables.forEach((v: any) => {
      const y = parseYearSuffix(v.variable);
      if (y && !distinctYears.includes(y)) distinctYears.push({year:y, obj:v});
    });
    distinctYears.sort((a, b) => a.year - b.year);

    // Initialize yMap arrays
    distinctYears.forEach((yr) => {
      yMap[yr.year] = [];
    });

    // Build line data for each record (your snippet)
    const lines: any[] = [];
    for (const [recordId, recordVals] of Object.entries<any>(data.dataJSON)) {
      const linePoints: { year: number; value: number | null, var: any, descriptor: string }[] = [];
      options.variables.forEach((v: any) => {
        const y = parseYearSuffix(v.variable);
        if (!y) return;
        const rawVal = recordVals[v.variable];
        let numericVal: number | null = parseFloat(rawVal);
        if (isNaN(numericVal)) numericVal = null;
        if (numericVal !== null) {
          if (numericVal < minVal) minVal = numericVal;
          if (numericVal > maxVal) maxVal = numericVal;
          // Also push into yMap for that year
          yMap[y].push(numericVal);
        }
        //console.log("A040725 v", v);
        linePoints.push({ year: y, value: numericVal, var: v.variable, descriptor: v.sort.DESCRIPTOR });
      });
      linePoints.sort((a, b) => a.year - b.year);
      lines.push({ id: recordId, points: linePoints });
    }

    // Build the NYC average line
    const avgPointsOnFly: { year: number; value: number | null }[] = [];
    const avgPoints: { year: number; value: number | null, descriptor:string }[] = [];
    options.variables.forEach((v: any) => {
      const y = parseYearSuffix(v.variable);
      if (!y) return;
      const avgVal = data?.meta?.avg?.[v.variable];
      let numericVal = !isNaN(avgVal) ? avgVal : null;
      if (numericVal !== null) {
        if (numericVal < minVal) minVal = numericVal;
        if (numericVal > maxVal) maxVal = numericVal;
      }
      avgPointsOnFly.push({ year: y, value: numericVal });
      avgPoints.push({ year: y, value: nycData.dataJSON[0][v.variable], descriptor: v.sort.DESCRIPTOR });
    });
    avgPoints.sort((a, b) => a.year - b.year);

    // Fallback if no valid data
    if (minVal === Number.POSITIVE_INFINITY) minVal = 0;
    if (maxVal === Number.NEGATIVE_INFINITY) maxVal = 10;

    // Add a small buffer so lines aren't on the chart edge
    const range = maxVal - minVal;
    if (range > 0) {
      minVal -= range * 0.05;
      maxVal += range * 0.05;
    }

    // Sort each year’s values so we can do rank-based coloring
    Object.keys(yMap).forEach((yrStr) => {
      const yrNum = parseInt(yrStr, 10);
      yMap[yrNum].sort((a, b) => a - b);
    });

    setAllLineData(lines);
    console.log("A040625 COT avgPoints", avgPoints);
    console.log("A040625 COT data",  data);
    console.log("A040625 COT nycData",  nycData);
    setAvgLineData(avgPoints);
    setYears(distinctYears);
    setGlobalMinValue(minVal);
    setGlobalMaxValue(maxVal);
    setYearValueMap(yMap);
  };

  // We'll treat the x-axis as a continuous scale from minYear..maxYear
  const minYearVal = years.length ? years[0].year : 0;
  const maxYearVal = years.length ? years[years.length - 1].year : 1;
  const _yearRange = maxYearVal - minYearVal || 1;

  // The "plot" area inside the margins
  const chartWidthInner = SVG_BASE_WIDTH - MARGIN_LEFT - MARGIN_RIGHT;
  const chartHeightInner = SVG_BASE_HEIGHT - MARGIN_TOP - MARGIN_BOTTOM;

  // Convert a year to an X position (reflecting actual gaps)
  const getXPos = (year: number) => {
    const ratio = (year - minYearVal) / _yearRange;
    return MARGIN_LEFT + ratio * chartWidthInner;
  };

  // Convert a data value to a Y position (from globalMinValue..globalMaxValue)
  const getYPos = (val: number | null) => {
    if (val === null) return MARGIN_TOP + chartHeightInner;
    const domain = globalMaxValue - globalMinValue || 1;
    const ratio = (val - globalMinValue) / domain;
    return MARGIN_TOP + (chartHeightInner - ratio * chartHeightInner);
  };

  // Build a path string for a line's points
  const buildLinePath = (points: { year: number; value: number | null }[]) => {
    if (!points?.length) return '';
    return points.map((pt, i) => {
      const x = getXPos(pt.year);
      const y = getYPos(pt.value);
      return i === 0 ? `M ${x},${y}` : `L ${x},${y}`;
    }).join(' ');
  };

  // Decide color for the line path (for normal, selected, hovered lines)
  const getLineColor = (recordId: string) => {
    if (recordId === hoveredId) return Colors.highlightHover;
    if (recordId === selectedId) return Colors.highlightSelect;
    return '#ccc';
  };

  // We want to draw lines in a specific order:
  // 1) normal lines (gray), 2) selected line (black), 3) NYC average (blue), 4) hovered line (pink) on top
  const normalLines = allLineData.filter(
    (l: any) => l.id !== hoveredId && l.id !== selectedId
  );
  const selectedLineObj = allLineData.find((l: any) => l.id === selectedId);

  // On click, set selected
  const handleLineClick = (recordId: string, pt: any) => {
    console.log("A041025 COT handleLineClick > recordId, pt?.var", recordId, pt?.var);
    onClickHistogram(recordId, "cot", options.variables[0].sort.moduleId);
    if (options) {
      if (activePage !== "dashboard") {
        if (activePage === "stories") {
          onSetActiveSTModuleId(options.variables[0].sort.moduleId);
        } else if (activePage === "rg") {
          onSetActiveRGModuleId(options.variables[0].sort.moduleId);
        }else{
          onSetActiveModuleId(options.variables[0].sort.moduleId);
        }
      } else {
        onSetActiveDBModuleId(options.variables[0].sort.moduleId);
      }
    }
    // We dont want to set the active indicator here, it is not disambiguated by year on the full line
    /*if (pt && pt.var) {
        onSetActiveIndicator(pt.var, options.type.toLowerCase(), options.variables[0].sort.moduleId);
        const matchedVariable = options.variables.find((v: any) => v.variable === pt.var);
        if (matchedVariable) {
            setSelectedIndicator(matchedVariable);
        }
    }*/
  };
  // On click, set selected
  const handleCircleClick = (recordId: string | null, pt: any) => {
    console.log("A042225 COT handleCircleClick > recordId, pt", recordId, pt);
    if (pt && pt.year) {
      setSelectedYear(pt.year);
    }else{
      setSelectedYear(null);
    }
    if (recordId){
      onClickHistogram(recordId, "cot", options.variables[0].sort.moduleId);
    }
    if (options) {
      if (activePage !== "dashboard") {
        if (activePage === "stories") {
          onSetActiveSTModuleId(options.variables[0].sort.moduleId);
        }else if (activePage === "rg") {
          onSetActiveRGModuleId(options.variables[0].sort.moduleId);
        }else{
          onSetActiveModuleId(options.variables[0].sort.moduleId);
        }
      } else {
        onSetActiveDBModuleId(options.variables[0].sort.moduleId);
      }
    }
    if (pt && pt.var) {
        onSetActiveIndicator(pt.var, options.type.toLowerCase(), options.variables[0].sort.moduleId);
        const matchedVariable = options.variables.find((v: any) => v.variable === pt.var);
        if (matchedVariable) {
            setSelectedIndicator(matchedVariable);
        }
    }
  };

  // On line/circle hover, set hovered
  const handleLineMouseEnter = (recordId: string) => {
    onHoverHistogram(recordId);
  };
  const handleLineMouseLeave = () => {
    onHoverHistogram('');
  };

  // Compute tooltip position relative to container bounds
  const handleCircleMouseEnter = (e: React.MouseEvent<SVGCircleElement>, obj: any) => {
    if (!containerRef.current) return;
    const containerRect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - containerRect.left;
    const y = e.clientY - containerRect.top;
    setTooltip({ x, y, obj });
    if (obj.recordId !== "NYC"){
      onHoverHistogram(obj.recordId);
    }
  };
  const handleCircleMouseMove = (e: React.MouseEvent<SVGCircleElement>) => {
    if (!containerRef.current) return;
    const containerRect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - containerRect.left;
    const y = e.clientY - containerRect.top;
    setTooltip((prev) => prev && { ...prev, x, y });
  };
  const handleCircleMouseLeave = () => {
    setTooltip(null);
    onHoverHistogram('');
  };

  /**
   * Decide the color for a dot:
   *  - If line is hovered => pink (Colors.highlightHover)
   *  - If line is selected => black (Colors.highlightSelect)
   *  - Else => use quintile color based on that dot’s rank in the year’s distribution
   */
  const getDotColor = (recordId: string, year: number, value: number | null) => {
    if (recordId === hoveredId) return Colors.highlightHover;
    if (recordId === selectedId) return Colors.highlightSelect;
    if (value === null) {
      return '#ccc'; // fallback
    }
    const yearVals = yearValueMap[year] || [];
    const rankIndex = yearVals.indexOf(value);
    if (rankIndex < 0) {
      return '#ccc';
    }
    return Colors.getColorQuintile(rankIndex, 0, yearVals.length, colorClass);
  };

  {console.log("A040625 COT tooltip", tooltip?.obj)}
  {console.log("A040625 options?.variables", options?.variables)}
  return (
    <div ref={containerRef} style={{ width: '100%', position: 'relative' }}>
      {/* Left area with COTMeta */}
      <div style={{
        width: '150px',
        float: 'left',
        fontSize: 12,
        lineHeight: '14px',
        marginRight: '10px'
      }}>
        <COTMeta
          dataArray={data.dataArray}
          data={data}
          options={options}
          indicator={selectedIndicator}
          mostChangeActive={mostChangeActive} 
          setMostChangeActive={setMostChangeActive}
          leastChangeActive={leastChangeActive}
          setLeastChangeActive={setLeastChangeActive}
          yearRange={yearRange}
          setYearRange={setYearRange}
          change={change}
          setChange={setChange}
          year={selectedYear}
          onSetActiveIndicator={onSetActiveIndicator}
          setSelectedIndicator={setSelectedIndicator}
        />
      </div>

      {/* Main chart area */}
      <div style={{ marginLeft: '160px', height: '375px' }}>
        <svg
          width="100%"
          viewBox={`0 0 ${SVG_BASE_WIDTH} ${SVG_BASE_HEIGHT}`}
          preserveAspectRatio="xMinYMin meet"
          style={{ display: 'block', overflow: 'visible', maxHeight:"400px" }}
        >
          {/* Vertical grid lines and year boxes */}
          {years.map((_yr, i) => {
            let yr = _yr.year;
            let _pt = {
              var:_yr.obj.variable,
              year:_yr.obj.sort.YEAR,
            }
            const x = getXPos(yr);
            const boxWidth = 38;
            const boxHeight = 23;
            const boxY = MARGIN_TOP + chartHeightInner + 15;
            const boxX = x - boxWidth / 2;
            return (
                <g
                key={`year-${yr}-${i}`}
                onClick={() => handleCircleClick(null, _pt)}
                style={{ cursor: 'pointer' }}
                >
                <line
                  x1={x}
                  y1={MARGIN_TOP}
                  x2={x}
                  y2={MARGIN_TOP + chartHeightInner}
                  stroke="#eee"
                  strokeWidth={1}
                />
                <rect
                  x={boxX}
                  y={boxY}
                  width={boxWidth}
                  height={boxHeight}
                  fill={selectedYear == yr ? "#000" : "#fff"}
                  stroke="#000"
                  rx={4}
                  ry={4}
                />
                <text
                  x={x}
                  y={boxY + boxHeight * 0.7}
                  fontSize={12}
                  textAnchor="middle"
                  fill={selectedYear == yr ? "#fff" : "#000"}
                >
                  {yr}
                </text>
              </g>
            );
          })}

          {/* Y ticks as short black ticks (no full horizontal grid lines) */}
          {drawYTicks(globalMinValue, globalMaxValue).map((tickVal) => {
            const y = getYPos(tickVal);
            return (
              <g key={`y-tick-${tickVal}`}>
                <line
                  x1={MARGIN_LEFT - 28}
                  y1={y}
                  x2={MARGIN_LEFT - 22}
                  y2={y}
                  stroke="#000"
                  strokeWidth={1}
                />
                <text
                  x={MARGIN_LEFT - 36}
                  y={y + 4}
                  fontSize={12}
                  textAnchor="end"
                  fill="#000"
                >
                  {d2gRound(tickVal, { DESCRIPTOR: '' })}
                </text>
              </g>
            );
          })}

          {/* 1) Normal lines (gray) */}
          {normalLines.map((record, i) => {
            //console.log("F040825 record", record);
            let filteredOut = false;
            if (selected10Ids && selected10Ids.length > 0 && !selected10Ids.some((id: any) => id.geoId === record.id)) {
              // We're filtering out all but ten lines
              filteredOut = true;
              //console.log("F040825 filtered record ======> ", record);
              //console.log("F040825 selected10Ids ======> ", selected10Ids);
            }
            const pathD = buildLinePath(record.points);
            return (<g key={`normal-${record.id}`}>
              {!filteredOut
              ? <g
                  key={`normal-${record.id}-${i}`}
                  onMouseEnter={() => handleLineMouseEnter(record.id)}
                  onMouseLeave={handleLineMouseLeave}
                >
                  <path
                    d={pathD}
                    fill="none"
                    stroke={getLineColor(record.id)}
                    strokeWidth={1.2}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleLineClick(record.id, null)}
                  />
                  {record.points.map((pt: any, i2: number) => {
                    if (pt.value === null) return null;
                    const cx = getXPos(pt.year);
                    const cy = getYPos(pt.value);
                    const dotColor = getDotColor(record.id, pt.year, pt.value);
                    return (
                      <circle
                        key={`pt-${record.id}-${i2}`}
                        cx={cx}
                        cy={cy}
                        r={3}
                        fill={dotColor}
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleCircleClick(record.id, pt)}
                        onMouseEnter={(e) =>
                          handleCircleMouseEnter(e, {
                            recordId: record.id,
                            year: pt.year,
                            value: pt.value, 
                            descriptor: pt.descriptor
                          })
                        }
                        onMouseMove={handleCircleMouseMove}
                        onMouseLeave={handleCircleMouseLeave}
                      />
                    );
                  })}
                </g>
              : null}</g>
            );
          })}

          {/* 2) Selected line (black), if any */}
          {selectedLineObj && (() => {
            let filteredOut = false;
            if (selected10Ids && selected10Ids.length > 0 && !selected10Ids.some((id: any) => id.geoId === selectedLineObj.id)) {
              filteredOut = true;
            }
            const pathD = buildLinePath(selectedLineObj.points);
            return (<>{
              !filteredOut
              ? <g
                  key={`selected-${selectedLineObj.id}`}
                  onMouseEnter={() => handleLineMouseEnter(selectedLineObj.id)}
                  onMouseLeave={handleLineMouseLeave}
                >
                  <path
                    d={pathD}
                    fill="none"
                    stroke="#000"
                    strokeWidth={2}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleLineClick(selectedLineObj.id, null)}
                  />
                  {selectedLineObj.points.map((pt: any, i2: number) => {
                    if (pt.value === null) return null;
                    const cx = getXPos(pt.year);
                    const cy = getYPos(pt.value);
                    const dotColor = '#000';
                    return (
                      <circle
                        key={`pt-${selectedLineObj.id}-${i2}`}
                        cx={cx}
                        cy={cy}
                        r={4}
                        fill={dotColor}
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleCircleClick(selectedLineObj.id, pt)}
                        onMouseEnter={(e) =>
                          handleCircleMouseEnter(e, {
                            recordId: selectedLineObj.id,
                            year: pt.year,
                            value: pt.value, 
                            descriptor: pt.descriptor
                          })
                        }
                        onMouseMove={handleCircleMouseMove}
                        onMouseLeave={handleCircleMouseLeave}
                      />
                    );
                  })}
                </g>
              : null}
              </>
            );
          })()}

          {/* 3) NYC average (blue) */}
          {avgLineData.length > 0 && (
            <g>
              <path
                d={buildLinePath(avgLineData)}
                fill="none"
                stroke="#4691CE"
                strokeWidth={2}
                style={{ pointerEvents: 'none' }} // still disable hover on the line itself
              />
              {avgLineData.map((pt, i3) => {
                if (pt.value === null) return null;
                const cx = getXPos(pt.year);
                const cy = getYPos(pt.value);
                return (
                  <circle
                    key={`avg-pt-${i3}`}
                    cx={cx}
                    cy={cy}
                    r={3}
                    fill="#4691CE"
                    style={{ cursor: 'pointer' }} // removed pointerEvents and added pointer events
                    onClick={() => {}}
                    onMouseEnter={(e) =>
                      handleCircleMouseEnter(e, {
                        recordId: "NYC",
                        year: pt.year,
                        value: pt.value,
                        descriptor: pt.descriptor, //"NYC Average"
                      })
                    }
                    onMouseMove={handleCircleMouseMove}
                    onMouseLeave={handleCircleMouseLeave}
                  />
                );
              })}
              {avgLineData.length > 0 && avgLineData[0].value !== null && (
                <text
                  x={MARGIN_LEFT - 6}
                  y={getYPos(avgLineData[0].value)}
                  fill="#4691CE"
                  fontSize={12}
                  alignmentBaseline="middle"
                  textAnchor="end"
                  style={{ fontWeight: 'normal', pointerEvents: 'none' }}
                >
                  NYC
                </text>
              )}
            </g>
          )}

          {/* 4) Hovered line (pink) */}
          {localHoveredLine && localHoveredLine.id !== selectedId && (() => {
            const pathD = buildLinePath(localHoveredLine.points);
            return (
              <g
                key={`hovered-${localHoveredLine.id}`}
                onMouseEnter={() => handleLineMouseEnter(localHoveredLine.id)}
                onMouseLeave={handleLineMouseLeave}
              >
                <path
                  d={pathD}
                  fill="none"
                  stroke={Colors.highlightHover}
                  strokeWidth={2.5}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleLineClick(localHoveredLine.id, null)}
                />
                {localHoveredLine.points.map((pt: any, i2: number) => {
                  if (pt.value === null) return null;
                  const cx = getXPos(pt.year);
                  const cy = getYPos(pt.value);
                  return (
                    <circle
                      key={`pt-${localHoveredLine.id}-${i2}`}
                      cx={cx}
                      cy={cy}
                      r={4}
                      fill={Colors.highlightHover}
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleCircleClick(localHoveredLine.id, pt)}
                      onMouseEnter={(e) =>
                        handleCircleMouseEnter(e, {
                          recordId: localHoveredLine.id,
                          year: pt.year,
                          value: pt.value,
                          descriptor: pt.descriptor
                        })
                      }
                      onMouseMove={handleCircleMouseMove}
                      onMouseLeave={handleCircleMouseLeave}
                    />
                  );
                })}
              </g>
            );
          })()}
        </svg>

        {/* Tooltip */}
        {tooltip && (
          <div
            style={{
              position: 'absolute',
              top: tooltip.y - 60,
              left: tooltip.x - 40,
              backgroundColor: '#fff',
              border: '2px solid #ccc',
              borderRadius: '6px',
              padding: '8px 12px',
              pointerEvents: 'none',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
              zIndex: 10,
              color: '#333',
              width: '75px',
              height: '50px',
              textAlign: 'left',
              wordBreak: 'break-word',
              whiteSpace: 'normal',
              maxWidth: '75px',
              lineHeight: '0.9'
            }}
          >
            <b>{d2gRound(tooltip.obj.value, { DESCRIPTOR: tooltip.obj.descriptor })}</b>
            <span style={{ fontSize: '10px', color: '#666' }}>
              {tooltip.obj.recordId === "NYC"
                ? ` (${tooltip.obj.recordId})` 
                : ` (${getTooltipGeoText(data.dataJSON, activeGeography, tooltip.obj.recordId)})` }
            </span>
            <div
              style={{
                position: 'absolute',
                bottom: '-7px',
                left: '45%',
                width: '12px',
                height: '12px',
                backgroundColor: '#fff',
                borderLeft: '2px solid #ccc',
                borderBottom: '2px solid #ccc',
                transform: 'rotate(-45deg)',
                boxShadow: '-1px 1px 2px rgba(0, 0, 0, 0.1)'
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Utility to create an array of tick values from `minVal..maxVal`.
 */
function drawYTicks(minVal: number, maxVal: number): number[] {
  const ticks: number[] = [];
  if (minVal > maxVal) return ticks;
  const steps = 5;
  const range = maxVal - minVal;
  if (range === 0) {
    return [minVal];
  }
  const stepSize = range / steps;
  for (let i = 0; i <= steps; i++) {
    ticks.push(minVal + i * stepSize);
  }
  return ticks;
}

export default COT;
